import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { getPostLoginPath, isAssessmentStudent } from '../utils/assessmentStudent';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useUser();
  const hasFetched = useRef(false);
  const usernameRef = useRef(null);

  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  }, []);

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/auth/check-captcha', { withCredentials: true });
        // if (!res.data.valid) {
        fetchCaptcha();
        // }
      } catch (err) {
        console.error('Error checking CAPTCHA validity:', err);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCaptcha = async () => {
    setCaptchaAnswer('');
    try {
      setCaptchaLoading(true);
      await api.get('/auth/reset-captcha', { withCredentials: true });
      const res = await api.get('/auth/show-login-page', { withCredentials: true });
      setCaptchaQuestion(res.data.captchaQuestion);
    } catch (err) {
      setErrorMessage('Failed to refresh CAPTCHA. Please try again.');
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCaptcha();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    const trimmedCaptchaAnswer = captchaAnswer.trim();

    if (trimmedUsername === '') {
      setErrorMessage('Username is required');
      setLoading(false);
      return;
    }
    if (trimmedPassword === '') {
      setErrorMessage('Password is required');
      setLoading(false);
      return;
    }
    if (trimmedCaptchaAnswer === '') {
      setErrorMessage('Please answer the CAPTCHA');
      setLoading(false);
      return;
    }

    try {
      const res = await api.post('/auth/login', {
        user_name: trimmedUsername,
        password: trimmedPassword,
        captcha: trimmedCaptchaAnswer,
      }, { withCredentials: true });

      if (res.data?.messages?.error) {
        setErrorMessage(res.data.messages.error);
        return;
      }

      const { token, user } = res.data;
      localStorage.setItem('token', token);

      const res2 = await api.get('/auth/profile', {
        withCredentials: true,   // ⭐ MANDATORY FOR APACHE
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(res2.data);

      // Redirect: use saved redirectTo (from email link) or default based on role
      const redirectTo = searchParams.get('redirectTo');
      if (redirectTo) {
        navigate(decodeURIComponent(redirectTo));
      } else {
        const destination = res2.data.role_id == 2 ? '/assessment' : '/home';
        navigate(destination);
      }

      if (isAssessmentStudent(res2.data)) {
        const studentHome = getPostLoginPath(res2.data);
        const rawRedirect = redirectTo ? decodeURIComponent(redirectTo).split('?')[0].split('#')[0] : '';
        const low = rawRedirect.toLowerCase();
        const allowRedirect = rawRedirect && (low.startsWith('/assessment') || low.startsWith('/execution/'));
        navigate(allowRedirect ? rawRedirect : studentHome, { replace: true });
      }
      
    } catch (err) {
      fetchCaptcha();
      const errorMsg = err?.response?.data?.messages?.error;
      setErrorMessage(errorMsg || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }

  };

  return (
    <>
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Side (Illustration) */}
        <div className="w-full md:w-1/2 bg-white flex justify-center items-center p-4 relative">
          <div className="text-center">
            <img src="/login_page.svg" alt="Helpdesk Illustration" className="w-full max-w-md mx-auto" />
            {/* <div className="mt-6">
          <img src="/logo.png" alt="ENOVA Logo" className="h-10 mx-auto" />
          <h1 className="text-lg font-bold text-[#B91C1C] mt-2">HELP TICKETING SYSTEM</h1>
        </div> */}
          </div>
          <span className="absolute top-2 left-4 text-xs text-[#B91C1C] font-semibold hidden">Welcome to ENOVA</span>

        </div>

        {/* Right Side (Login Form) */}
        <div className="w-full md:w-1/2 flex items-center justify-center bg-white p-4">
          <form onSubmit={handleLogin} className="w-fit max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 text-left mb-1">Welcome Back 👋</h2>
            <p className="text-sm text-gray-600 mb-6">
              Today is a new day. It’s your day. You shape it. <br />
              Sign in to start managing your projects.
            </p>

            <label className="block mb-1 text-sm font-medium text-gray-700">User Name</label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Example@email.com"
              ref={usernameRef}
              className="w-full px-4 py-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-red-200"
            />

            <label className="block mb-1 text-sm font-medium text-gray-700">Password</label>
            <div className="relative mb-3">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-200"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 cursor-pointer text-gray-500 hover:text-gray-700"
                title={showPassword ? 'Hide Password' : 'Show Password'}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </span>
            </div>


            <div className="mb-4 text-right">
              <a href="/forgot-password" className="text-sm text-[#3B82F6] hover:underline font-medium">
                Forgot Password?
              </a>
            </div>

            <label className="block mb-1 text-sm font-medium text-gray-700">
              Captcha: {captchaQuestion} =
            </label>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="number"
                required
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="Answer"
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-200"
              />
              <button
                type="button"
                onClick={fetchCaptcha}
                className="p-1 text-gray-500 hover:text-red-700 focus:outline-none"
                disabled={captchaLoading}
                title="Refresh CAPTCHA"
              >
                <ArrowPathIcon className={`w-5 h-5 ${captchaLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {errorMessage && (
              <div className="text-red-600 font-medium text-sm text-center mb-2">{errorMessage}</div>
            )}

            <button
              type="submit"
              disabled={loading || captchaLoading}
              className="w-full bg-[#B91C1C] hover:bg-red-800 text-white font-semibold py-2 rounded-lg flex justify-center items-center gap-2"
            >
              {loading && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-50" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-100"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              )}
              <span>{loading ? 'Logging in...' : 'Login'}</span>
            </button>
          </form>

        </div>
      </div>
      <div className="w-full">
        <div
          className="text-center text-xs text-gray-500 mt-6
               md:fixed md:bottom-2 md:left-4 md:mt-0"
        >
          © {new Date().getFullYear()} ENOVA Software & Hardware Solutions Pvt Ltd
        </div>

        <div
          className="text-center text-xs mt-2
               md:mt-0 md:fixed md:bottom-2 md:right-4"
        >
          Need help?{" "}
          <a
            href="tel:9345636682"
            className="text-[#B91C1C] font-semibold hover:underline"
          >
            9345636682
          </a>
        </div>
      </div>

    </>
  );

};

export default Login;
